import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import CampaignIcon from "@mui/icons-material/Campaign";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import TimerIcon from "@mui/icons-material/Timer";
import GraphicEqIcon from "@mui/icons-material/GraphicEq";
import EditNotificationsIcon from "@mui/icons-material/EditNotifications";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

import socketService from "../socket/newsocketService";
import { getDatabase, ref, onValue, off } from "firebase/database";
import apiClient from "../Utils/apiClient";
import { audioAlertService } from "../Utils/audioService";

const BROADCAST_INTERVAL_SECONDS = 300; // 5 minutes in seconds

/**
 * VoiceNotificationBar
 * 
 * Top-level voice notification bar broadcast to admin staff.
 * Broadcasts voice announcements every 5 minutes.
 * The voice loops continuously and ONLY stops when admin staff clicks Mute.
 * Also broadcasts user responses and escort emergency alerts.
 */
const VoiceNotificationBar = ({
  incomingAlert = "",
  isMuted: controlledMuted = null,
  onToggleMute: controlledToggleMute = null,
  stopVoiceTrigger = 0,
  hasActiveTrips = true,
  activeEscortCount = null,
}) => {
  const [internalMuted, setInternalMuted] = useState(false);
  const isMuted = controlledMuted !== null ? controlledMuted : internalMuted;

  const [isSpeaking, setIsSpeaking] = useState(false);
  const [countdown, setCountdown] = useState(BROADCAST_INTERVAL_SECONDS);
  const [currentMessage, setCurrentMessage] = useState(
    incomingAlert || "All security and escort monitoring systems are operational."
  );
  const [latestSource, setLatestSource] = useState(
    incomingAlert ? "User Response Alert" : "System Status"
  );
  const [isCustomDialogOpen, setIsCustomDialogOpen] = useState(false);
  const [customInputText, setCustomInputText] = useState("");
  const [isBarMinimized, setIsBarMinimized] = useState(false);

  const isMutedRef = useRef(isMuted);
  const loopTimeoutRef = useRef(null);
  const utteranceRef = useRef(null);
  const currentMessageRef = useRef(currentMessage);
  const countdownIntervalRef = useRef(null);
  const recentNotifsRef = useRef([]);
  const prevIncomingAlertRef = useRef("");
  const hasActiveTripsRef = useRef(hasActiveTrips);

  // Sync ref with mute state
  useEffect(() => {
    isMutedRef.current = isMuted;
    if (isMuted) {
      if (loopTimeoutRef.current) {
        clearTimeout(loopTimeoutRef.current);
        loopTimeoutRef.current = null;
      }
      if (typeof window !== "undefined" && "speechSynthesis" in window) {
        window.speechSynthesis.cancel();
      }
      setIsSpeaking(false);
    }
  }, [isMuted]);

  useEffect(() => {
    currentMessageRef.current = currentMessage;
  }, [currentMessage]);

  // Unlock audio safely on first interaction
  const unlockAudioContext = useCallback(() => {
    if (audioAlertService && !audioAlertService.isUnlocked) {
      audioAlertService.unlockAudio();
    }
  }, []);

  // Stop speech
  const stopSpeech = useCallback(() => {
    if (loopTimeoutRef.current) {
      clearTimeout(loopTimeoutRef.current);
      loopTimeoutRef.current = null;
    }
    if (typeof window !== "undefined" && "speechSynthesis" in window) {
      try {
        window.speechSynthesis.cancel();
      } catch (_) {}
    }
    setIsSpeaking(false);
  }, []);

  // Reset to default operational message when trip ends
  const resetToDefault = useCallback(() => {
    if (loopTimeoutRef.current) {
      clearTimeout(loopTimeoutRef.current);
      loopTimeoutRef.current = null;
    }
    if (typeof window !== "undefined" && "speechSynthesis" in window) {
      try {
        window.speechSynthesis.cancel();
      } catch (_) {}
    }
    setIsSpeaking(false);
    setCurrentMessage("All security and escort monitoring systems are operational.");
    currentMessageRef.current = "All security and escort monitoring systems are operational.";
    setLatestSource("System Status");
    prevIncomingAlertRef.current = "";
  }, []);

  // Track active trips state
  useEffect(() => {
    const isActive = Boolean(hasActiveTrips && (activeEscortCount === null || activeEscortCount > 0));
    hasActiveTripsRef.current = isActive;
    if (!isActive) {
      stopSpeech();
      resetToDefault();
    }
  }, [hasActiveTrips, activeEscortCount, stopSpeech, resetToDefault]);

  // Stop voice whenever parent signals that an escort ended
  useEffect(() => {
    if (stopVoiceTrigger > 0) {
      console.log("[VoiceNotificationBar] stopVoiceTrigger fired, ending voice notification.");
      stopSpeech();
      resetToDefault();
    }
  }, [stopVoiceTrigger, stopSpeech, resetToDefault]);

  // Listen to global stopEscortVoice events
  useEffect(() => {
    const handleStopVoiceEvent = (event) => {
      console.log("[VoiceNotificationBar] stopEscortVoice event received:", event?.detail);
      stopSpeech();
      resetToDefault();
    };

    window.addEventListener("stopEscortVoice", handleStopVoiceEvent);
    return () => {
      window.removeEventListener("stopEscortVoice", handleStopVoiceEvent);
    };
  }, [stopSpeech, resetToDefault]);

  // Listen to socket escort ended events directly
  useEffect(() => {
    const handleEscortEnded = (data) => {
      console.log("[VoiceNotificationBar] Escort ended event received from socket:", data);
      stopSpeech();
      resetToDefault();
    };

    const handleShortDistanceAdmin = (data) => {
      if (
        data?.action === "endescort" ||
        data?.action === "ended" ||
        data?.action === "delete" ||
        data?.action === "resolved" ||
        data?.status === "ended" ||
        data?.escortStatus === "ended"
      ) {
        handleEscortEnded(data);
      }
    };

    socketService.on("shortdistanceescortadmin", handleShortDistanceAdmin);
    socketService.on("driverEscortEnded", handleEscortEnded);
    socketService.on("driverEscortDeleted", handleEscortEnded);
    socketService.on("virtualescort_resolved", handleEscortEnded);
    socketService.on("virtualescort_ended", handleEscortEnded);
    socketService.on("endescort", handleEscortEnded);
    socketService.on("escort_ended", handleEscortEnded);
    socketService.on("escortEnded", handleEscortEnded);

    return () => {
      socketService.off("shortdistanceescortadmin", handleShortDistanceAdmin);
      socketService.off("driverEscortEnded", handleEscortEnded);
      socketService.off("driverEscortDeleted", handleEscortEnded);
      socketService.off("virtualescort_resolved", handleEscortEnded);
      socketService.off("virtualescort_ended", handleEscortEnded);
      socketService.off("endescort", handleEscortEnded);
      socketService.off("escort_ended", handleEscortEnded);
      socketService.off("escortEnded", handleEscortEnded);
    };
  }, [stopSpeech, resetToDefault]);

  /**
   * Speak broadcast text.
   * User responses speak cleanly and only critical emergency alerts loop.
   * All voice notifications strictly end when the escort ends.
   */
  const speakBroadcastLoop = useCallback((text) => {
    if (isMutedRef.current || !text || !text.trim() || !hasActiveTripsRef.current) {
      setIsSpeaking(false);
      return;
    }

    if (typeof window === "undefined" || !("speechSynthesis" in window)) {
      console.warn("[VoiceNotificationBar] SpeechSynthesis not supported in this browser.");
      return;
    }

    // Stop previous utterance
    window.speechSynthesis.cancel();
    if (loopTimeoutRef.current) {
      clearTimeout(loopTimeoutRef.current);
      loopTimeoutRef.current = null;
    }

    setIsSpeaking(true);

    const utterance = new SpeechSynthesisUtterance(text);
    utteranceRef.current = utterance;
    utterance.rate = 0.95;
    utterance.pitch = 1.0;
    utterance.volume = 1.0;
    utterance.lang = "en-US";

    const isCriticalDanger =
      text.toLowerCase().includes("danger") || text.toLowerCase().includes("emergency");

    utterance.onend = () => {
      // Loop ONLY if staff has not muted it AND escort trips are active AND it's a critical danger alert
      if (!isMutedRef.current && hasActiveTripsRef.current && isCriticalDanger) {
        loopTimeoutRef.current = setTimeout(() => {
          if (!isMutedRef.current && hasActiveTripsRef.current) {
            speakBroadcastLoop(text);
          } else {
            setIsSpeaking(false);
          }
        }, 5000);
      } else {
        setIsSpeaking(false);
      }
    };

    utterance.onerror = (event) => {
      // Normal browser cancellation when superseded by a new broadcast, muted, or stopped
      if (event.error === "interrupted" || event.error === "canceled") {
        setIsSpeaking(false);
        return;
      }
      console.warn("[VoiceNotificationBar] Speech error:", event);
      if (!isMutedRef.current && hasActiveTripsRef.current && isCriticalDanger) {
        loopTimeoutRef.current = setTimeout(() => {
          if (!isMutedRef.current && hasActiveTripsRef.current) {
            speakBroadcastLoop(text);
          } else {
            setIsSpeaking(false);
          }
        }, 5000);
      } else {
        setIsSpeaking(false);
      }
    };

    try {
      window.speechSynthesis.speak(utterance);
    } catch (e) {
      console.warn("[VoiceNotificationBar] Failed to speak utterance:", e);
      setIsSpeaking(false);
    }
  }, []);

  /**
   * Triggers a broadcast announcement.
   */
  const triggerBroadcast = useCallback((text, source = "Admin Broadcast") => {
    const isManualTrigger = source === "Staff Manual Trigger" || source === "Manual Admin Broadcast";
    if (!hasActiveTripsRef.current && !isManualTrigger) {
      console.log("[VoiceNotificationBar] Broadcast suppressed: No active escort trips.", source);
      return;
    }

    const broadcastText = text && text.trim() ? text.trim() : currentMessageRef.current;
    setCurrentMessage(broadcastText);
    setLatestSource(source);
    setIsBarMinimized(false);

    // Reset countdown timer
    setCountdown(BROADCAST_INTERVAL_SECONDS);

    // If staff previously muted, keep muted until explicitly unmuted
    if (!isMutedRef.current) {
      speakBroadcastLoop(broadcastText);
    }
  }, [speakBroadcastLoop]);

  /**
   * Listen to incoming alerts passed via props (e.g. from User Response / Wellbeing response)
   */
  useEffect(() => {
    if (incomingAlert && incomingAlert.trim() && incomingAlert !== prevIncomingAlertRef.current) {
      prevIncomingAlertRef.current = incomingAlert;
      if (hasActiveTripsRef.current) {
        triggerBroadcast(incomingAlert, "User Response Alert");
      }
    }
  }, [incomingAlert, triggerBroadcast]);

  /**
   * Handle Staff Mute
   */
  const handleToggleMute = () => {
    unlockAudioContext();
    if (controlledToggleMute) {
      controlledToggleMute();
    } else {
      if (internalMuted) {
        setInternalMuted(false);
        isMutedRef.current = false;
        if (currentMessageRef.current) {
          speakBroadcastLoop(currentMessageRef.current);
        }
      } else {
        setInternalMuted(true);
        isMutedRef.current = true;
        stopSpeech();
      }
    }
  };

  /**
   * 1. 5-Minute Countdown & Auto-Broadcast Timer
   */
  useEffect(() => {
    countdownIntervalRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          // 5 minutes reached: ONLY broadcast if staff has not muted AND escort trips are currently active
          if (!isMutedRef.current && hasActiveTripsRef.current) {
            let announcement = "";

            if (currentMessageRef.current && latestSource === "User Response Alert") {
              announcement = `Attention Admin Staff. Scheduled 5-minute escort check: ${currentMessageRef.current}`;
            } else {
              announcement = `Attention Admin Staff. Scheduled 5-minute escort system check: Escort monitoring channels are active.`;
            }

            triggerBroadcast(announcement, "5-Min Scheduled Broadcast");
          }
          return BROADCAST_INTERVAL_SECONDS;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, [latestSource, triggerBroadcast]);

  /**
   * 2. Listen to Real-Time Socket Voice Alerts & User Responses
   */
  useEffect(() => {
    const handleVoiceAlert = (data) => {
      console.log("[VoiceNotificationBar] socket alert received:", data);
      const msg = data?.message || data?.text || data?.title;
      if (msg && hasActiveTripsRef.current) {
        triggerBroadcast(msg, "Urgent Escort Alert");
      }
    };

    const handleAdminBroadcast = (data) => {
      console.log("[VoiceNotificationBar] admin_broadcast received:", data);
      const msg = data?.message || data?.text;
      if (msg) {
        triggerBroadcast(msg, "Admin Staff Broadcast");
      }
    };

    socketService.on("virtualescort_voice_alert", handleVoiceAlert);
    socketService.on("admin_broadcast", handleAdminBroadcast);
    socketService.on("admin_voice_broadcast", handleAdminBroadcast);

    return () => {
      socketService.off("virtualescort_voice_alert", handleVoiceAlert);
      socketService.off("admin_broadcast", handleAdminBroadcast);
      socketService.off("admin_voice_broadcast", handleAdminBroadcast);
    };
  }, [triggerBroadcast]);

  /**
   * 3. Listen to Firebase Notifications
   */
  useEffect(() => {
    let notifRef = null;
    try {
      const db = getDatabase();
      notifRef = ref(db, "/notification");

      const unsubscribe = onValue(notifRef, (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.val();
          const items = Object.keys(data).map((k) => ({
            id: k,
            Title: data[k].title,
            Text: data[k].text,
            Time: data[k].currentTimeStamp || "",
            State: data[k].state || "",
          }));

          const sorted = items.sort(
            (a, b) => new Date(b.Time).getTime() - new Date(a.Time).getTime()
          );

          recentNotifsRef.current = sorted;
        }
      });

      return () => {
        if (notifRef) off(notifRef);
      };
    } catch (e) {
      console.warn("[VoiceNotificationBar] Firebase not configured or error:", e);
    }
  }, []);

  /**
   * 4. Initial Greeting / Active Message Check
   */
  useEffect(() => {
    const fetchActiveVoiceDeterrence = async () => {
      try {
        const response = await apiClient.get("/voice-deterrence/active", {
          params: { audience: "driver" },
        });
        const activeMessages = Array.isArray(response?.data) ? response.data : [];
        if (activeMessages.length > 0 && !incomingAlert) {
          const firstMsg = activeMessages[0]?.message || activeMessages[0]?.text;
          if (firstMsg) {
            setCurrentMessage(firstMsg);
          }
        }
      } catch (err) {
        // Fallback silently
      }
    };

    fetchActiveVoiceDeterrence();

    return () => {
      stopSpeech();
    };
  }, [incomingAlert, stopSpeech]);

  // Format countdown mm:ss
  const formatTime = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s < 10 ? "0" : ""}${s}`;
  };

  // Submit custom broadcast message
  const handleSendCustomBroadcast = () => {
    if (!customInputText.trim()) return;
    const textToBroadcast = `Notice to Admin Staff: ${customInputText.trim()}`;
    triggerBroadcast(textToBroadcast, "Manual Admin Broadcast");
    setIsCustomDialogOpen(false);
    setCustomInputText("");

    // Broadcast through socket to other connected admin staff
    try {
      socketService.emit("admin_broadcast", {
        message: textToBroadcast,
        timestamp: new Date().toISOString(),
      });
    } catch (err) {
      console.warn("Could not emit broadcast to socket:", err);
    }
  };

  return (
    <>
      <Box
        sx={{
          width: "100%",
          borderRadius: "10px",
          backgroundColor: isSpeaking ? "#991b1b" : "#0f172a",
          backgroundImage: isSpeaking
            ? "linear-gradient(90deg, #7f1d1d 0%, #b91c1c 50%, #7f1d1d 100%)"
            : "linear-gradient(90deg, #0f172a 0%, #1e293b 100%)",
          color: "#ffffff",
          border: isSpeaking ? "2px solid #ef4444" : "1px solid #334155",
          px: { xs: 1.5, sm: 2 },
          py: isBarMinimized ? 0.4 : 0.8,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1.2,
          boxShadow: isSpeaking
            ? "0 4px 14px rgba(220, 38, 38, 0.45)"
            : "0 2px 6px rgba(0, 0, 0, 0.15)",
          transition: "all 0.3s ease-in-out",
          position: "relative",
          zIndex: 5,
        }}
      >
        {/* Left Section: Badges & Live Status */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, minWidth: "fit-content" }}>
          <Chip
            icon={
              isSpeaking ? (
                <GraphicEqIcon sx={{ color: "#fff !important" }} />
              ) : (
                <CampaignIcon sx={{ color: "#fff !important" }} />
              )
            }
            label={isSpeaking ? "VOICE BROADCAST LIVE" : "VOICE NOTIFICATION BAR"}
            size="small"
            sx={{
              backgroundColor: isSpeaking ? "#dc2626" : "#2563eb",
              color: "#ffffff",
              fontWeight: 800,
              fontSize: "0.74rem",
              letterSpacing: "0.5px",
              boxShadow: isSpeaking ? "0 0 10px rgba(239,68,68,0.8)" : "none",
            }}
          />

          <Chip
            icon={<TimerIcon sx={{ color: "#94a3b8 !important", fontSize: "0.95rem" }} />}
            label={`5-Min Interval: ${formatTime(countdown)}`}
            size="small"
            sx={{
              backgroundColor: "rgba(255,255,255,0.08)",
              color: "#e2e8f0",
              fontSize: "0.72rem",
              fontWeight: 600,
              border: "1px solid rgba(255,255,255,0.15)",
            }}
          />

          {isMuted && (
            <Chip
              label="MUTED"
              size="small"
              sx={{
                backgroundColor: "rgba(239, 68, 68, 0.2)",
                color: "#fca5a5",
                fontSize: "0.7rem",
                fontWeight: 700,
                border: "1px solid rgba(239, 68, 68, 0.4)",
              }}
            />
          )}
        </Box>

        {/* Center Section: Message Display */}
        {!isBarMinimized && (
          <Box
            sx={{
              flex: 1,
              minWidth: { xs: "100%", md: "260px" },
              display: "flex",
              alignItems: "center",
              gap: 1,
              overflow: "hidden",
              px: 1,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: isSpeaking ? "#fef08a" : "#38bdf8",
                fontWeight: 700,
                textTransform: "uppercase",
                fontSize: "0.7rem",
                whiteSpace: "nowrap",
              }}
            >
              [{latestSource}]:
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: "#ffffff",
                fontSize: "0.82rem",
                fontWeight: isSpeaking ? 700 : 500,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                letterSpacing: "0.2px",
              }}
            >
              {currentMessage}
            </Typography>
          </Box>
        )}

        {/* Right Section: Controls & Mute Button */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            minWidth: "fit-content",
            ml: "auto",
          }}
        >
          {/* MUTE / UNMUTE BUTTON (CRITICAL REQUIREMENT) */}
          <Button
            variant="contained"
            onClick={handleToggleMute}
            startIcon={
              isMuted ? (
                <VolumeOffIcon sx={{ fontSize: "1.1rem" }} />
              ) : (
                <VolumeUpIcon sx={{ fontSize: "1.1rem" }} />
              )
            }
            sx={{
              backgroundColor: isSpeaking
                ? "#ffffff !important"
                : isMuted
                ? "#475569 !important"
                : "#dc2626 !important",
              color: isSpeaking ? "#991b1b !important" : "#ffffff !important",
              fontWeight: 800,
              fontSize: "0.76rem",
              padding: "4px 12px",
              borderRadius: "6px",
              textTransform: "none",
              boxShadow: isSpeaking
                ? "0 0 14px rgba(255,255,255,0.9)"
                : "0 2px 5px rgba(0,0,0,0.3)",
              "&:hover": {
                backgroundColor: isSpeaking
                  ? "#f8fafc !important"
                  : isMuted
                  ? "#64748b !important"
                  : "#b91c1c !important",
              },
            }}
          >
            {isSpeaking
              ? "MUTE VOICE (STOP)"
              : isMuted
              ? "UNMUTE VOICE"
              : "MUTE VOICE"}
          </Button>

          {/* Broadcast Now Button */}
          <Tooltip title="Trigger voice broadcast immediately (every 5 min scheduled)">
            <Button
              variant="outlined"
              size="small"
              onClick={() => triggerBroadcast(currentMessage, "Staff Manual Trigger")}
              startIcon={<PlayArrowIcon sx={{ fontSize: "1rem" }} />}
              sx={{
                borderColor: "rgba(255,255,255,0.3)",
                color: "#e2e8f0",
                fontSize: "0.74rem",
                fontWeight: 600,
                textTransform: "none",
                padding: "3px 10px",
                borderRadius: "6px",
                "&:hover": {
                  borderColor: "#ffffff",
                  backgroundColor: "rgba(255,255,255,0.1)",
                },
              }}
            >
              Broadcast Now
            </Button>
          </Tooltip>

          {/* Custom Message Announcement Button */}
          <Tooltip title="Compose custom announcement to broadcast to admin staff">
            <IconButton
              size="small"
              onClick={() => {
                unlockAudioContext();
                setIsCustomDialogOpen(true);
              }}
              sx={{
                color: "#94a3b8",
                border: "1px solid rgba(255,255,255,0.15)",
                borderRadius: "6px",
                padding: "4px",
                "&:hover": {
                  color: "#ffffff",
                  backgroundColor: "rgba(255,255,255,0.1)",
                },
              }}
            >
              <EditNotificationsIcon sx={{ fontSize: "1.1rem" }} />
            </IconButton>
          </Tooltip>

          {/* Minimize / Expand Bar Button */}
          <Tooltip title={isBarMinimized ? "Expand Notification Bar" : "Minimize Notification Bar"}>
            <IconButton
              size="small"
              onClick={() => setIsBarMinimized(!isBarMinimized)}
              sx={{
                color: "#94a3b8",
                padding: "4px",
                "&:hover": {
                  color: "#ffffff",
                },
              }}
            >
              {isBarMinimized ? (
                <KeyboardArrowDownIcon sx={{ fontSize: "1.1rem" }} />
              ) : (
                <KeyboardArrowUpIcon sx={{ fontSize: "1.1rem" }} />
              )}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Custom Broadcast Dialog */}
      <Dialog
        open={isCustomDialogOpen}
        onClose={() => setIsCustomDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: "bold", fontSize: "1.1rem", pb: 1 }}>
          Broadcast Announcement to Admin Staff
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
            Enter the message to broadcast aloud to all admin staff monitoring the panel.
            The voice will broadcast every 5 minutes and repeat continuously until staff mutes it.
          </Typography>

          <TextField
            autoFocus
            fullWidth
            multiline
            rows={3}
            label="Broadcast Message"
            placeholder="e.g. Attention staff: Monitor urgent SOS alert in Lagos zone immediately."
            value={customInputText}
            onChange={(e) => setCustomInputText(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setIsCustomDialogOpen(false)}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSendCustomBroadcast}
            disabled={!customInputText.trim()}
            sx={{
              backgroundColor: "#2563eb !important",
              color: "#ffffff !important",
              textTransform: "none",
              fontWeight: 700,
            }}
          >
            Start Broadcast
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default VoiceNotificationBar;
